import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery, useQuery } from '@apollo/client';

import { Fade } from '@mui/material';
import { PageWrapper } from '../components/partials';
import CreativeGalleryHeader from './components/creativeGalleryHeader/CreativeGalleryHeader';
import TutorialSteps from '../components/utilities/tutorialSteps/TutorialSteps';
import css from './page.css';
import EmptyStateIcon from '../resources/icons/creatives/empty_state.svg';
import NoDataIcon from '../resources/icons/creatives/no_data.svg';
import { useAsync, useLocalStorageState } from '../utils/customHooks';
import CreativePopup from './components/creativePopup/CreativePopup';
import { Reports } from '../services';
import { GET_DIMENSIONS_BY_CATEGORY, GET_USER_APPS, GET_USER_METRICS, GET_USER_SOURCES } from './queries';
import CreativeGalleryFilters from './components/creativeGalleryFilters/CreativeGalleryFilters';
import { getMainCreativesQueryParams, getMultiValuedDimensionsQueryParams } from './creativesQueryUtils';
import { createLatestRequestGuard } from './requestUtils';
import { blankPendingFields, formatCreatives, getOptionsList, hasValidMetrics } from './creativesFormattingUtils';
import CreativesLayout from './components/creativesLayout/CreativesLayout';
import {
    CardSections,
    CREATIVE_GALLERY_REPORT_TYPE,
    CreativeFilters,
    CREATIVES_TAB,
    DEFAULT_END_DATE,
    DEFAULT_METRICS,
    DEFAULT_START_DATE,
    GalleryContext,
    TRACK_EVENT_PREFIX,
} from './consts';
import { isObjectEmpty, trackMixpanelEvent } from '../utils/general';
import {
    ALL_METRICS_OPTIONS,
    containsLazyMetric,
    CREATIVES_CORE_METRICS_OPTIONS,
    getAvailableMetricsOptions,
    getEagerMetrics,
    getMetricsToFetch,
    SORTED_METRICS_OPTIONS,
} from './metricsUtils';
import { isCreativesGalleryReportRunning } from '../actions/reports';
import { useUrlParams } from '../utils/useUrlParamsHook';
import useCsvDownload from './useCsvDownload';
import useCohortFields from './useCohortFields';
import fieldVisibilityRules from '../selectors/reportsConfig/fieldVisibilityRules';
import BadFilterCombination from './components/badFilterCombination/BadFilterCombination';
import CreativeExplore from '../services/creativeExplore';
import CreativeInsightsTab from './components/creativeInsights/CreativeInsightsTab';
import { MIN_DATE_RANGE_DAYS, MIXPANEL_INSIGHTS_EVENT } from './components/creativeInsights/consts';
import { getAdminModeEnabled } from '../selectors/user';

// Local storage keys
const LOCAL_STORAGE_SELECTED_METRICS = 'creative_gallery_selected_metrics';
const LOCAL_STORAGE_SELECTED_COHORTS = 'creative_gallery_selected_cohorts';
const STEPS_AMOUNT = 3;

export const EMPTY_STATE_PARAMS = {
    icon: <EmptyStateIcon className={css.tutorialIcon} />,
    title: 'STATIC.PAGES.CREATIVE_GALLERY.EMPTY_STATE.TITLE',
    steps: [...Array(STEPS_AMOUNT)].map((_, index) => `STATIC.PAGES.CREATIVE_GALLERY.EMPTY_STATE.STEPS.${index + 1}`),
};

export const NO_DATA_PARAMS = {
    icon: <NoDataIcon className={css.tutorialIcon} />,
    title: 'STATIC.PAGES.CREATIVE_GALLERY.NO_DATA.TITLE',
    steps: [...Array(STEPS_AMOUNT)].map((_, index) => `STATIC.PAGES.CREATIVE_GALLERY.NO_DATA.STEPS.${index + 1}`),
};

const ReportsAPI = new Reports();
const CreativeExploreAPI = new CreativeExplore();

const getLoadingSections = (isLoadingDimensions, isAsyncLoading, haveCohortsChanged, hasAsyncFilters) => {
    const loadingSections = [];
    if (isLoadingDimensions && !isAsyncLoading) loadingSections.push(CardSections.MULTI_VALUED_DIMENSIONS);
    if (isAsyncLoading && !haveCohortsChanged && hasAsyncFilters) loadingSections.push(CardSections.METRICS);
    return loadingSections;
};

const CreativeGallery = () => {
    const dispatch = useDispatch();
    const { getParamsObject, setParamsObject, removeParam } = useUrlParams();
    const gridRef = useRef(null);
    const pageLoadingStartTime = useRef(new Date());
    const haveCohortsChanged = useRef(false);
    const tempCreatives = useRef();
    const originalMultiValuedDimensions = useRef();
    const lastMultiValuedDimensions = useRef();
    const fetchedMetricsRef = useRef(new Set());
    const requestGuard = useRef(createLatestRequestGuard());
    const inFlightSignatureRef = useRef(null);
    const pendingInitialDeferralRef = useRef(false);
    const initialLazyFillActiveRef = useRef(false);
    const latestCreativesRef = useRef([]);

    const [selectedTab, setSelectedTab] = useState(CREATIVES_TAB);
    const [isPageInitiated, setIsPageInitiated] = useState(false);
    const [insightsDownloadUrl, setInsightsDownloadUrl] = useState();

    const [dateRange, setDateRange] = useState({
        startDate: DEFAULT_START_DATE,
        endDate: DEFAULT_END_DATE,
    });

    const [app, setApp] = useState();
    const [source, setSource] = useState();
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [viewedCreativeHash, setViewedCreativeHash] = useState();
    const [selectedCohorts, setSelectedCohorts] = useState([]);
    const [filteredCreatives, setFilteredCreatives] = useState([]);
    const [isBadFilterCombination, setIsBadFilterCombination] = useState(false);
    const [fieldsMetadata, setFieldsMetadata] = useState({});
    const [openInsightsShelf, setOpenInsightsShelf] = useState(false);
    const [additionalFilters, setAdditionalFilters] = useState([]);

    const creativesFieldsRules = useSelector(state => field =>
        fieldVisibilityRules.creativeExplore(field, getAdminModeEnabled(state), state.user, false, false, [], false)
    );

    const [localStorageMetrics, setLocalStorageMetrics] = useLocalStorageState(LOCAL_STORAGE_SELECTED_METRICS, []);
    const [localStorageCohorts, setLocalStorageCohorts] = useLocalStorageState(LOCAL_STORAGE_SELECTED_COHORTS, []);

    const { loading: isLoadingSources, data: sourcesResponse } = useQuery(GET_USER_SOURCES, {
        variables: { reportType: CREATIVE_GALLERY_REPORT_TYPE },
    });

    const { loading: isLoadingApps, data: appsResponse } = useQuery(GET_USER_APPS);

    const { data: metricsResponse, loading: isLoadingMetrics } = useQuery(GET_USER_METRICS);

    const { cohortsOptions, cohortsGroupBy, isLoadingCohorts, cohortPeriods } = useCohortFields(
        metricsResponse,
        creativesFieldsRules
    );

    const [getDimensionsByCategory, { data: dimensionsByCategoryResponse }] = useLazyQuery(GET_DIMENSIONS_BY_CATEGORY, { fetchPolicy: 'cache-and-network' });

    const dimensionsByCategory = useMemo(() => JSON.parse(dimensionsByCategoryResponse?.dimensionsByCategory ?? '{}'), [
        dimensionsByCategoryResponse,
    ]);
    const apps = appsResponse?.userApps;
    const sources = sourcesResponse?.userSources;
    const appOptions = useMemo(() => getOptionsList(apps || [], 'icon'), [apps]);
    const sourceOptions = useMemo(() => getOptionsList(sources || [], 'logoPath'), [sources]);

    const metricsOptions = useMemo(() => {
        if (!metricsResponse) return [];

        const allMetrics = new Set(
            getAvailableMetricsOptions(metricsResponse.userMetrics, CREATIVES_CORE_METRICS_OPTIONS)
        );

        return SORTED_METRICS_OPTIONS.filter(({ value }) => allMetrics.has(value));
    }, [metricsResponse]);

    const fullMetricsToFetch = useMemo(() => getMetricsToFetch(metricsOptions, selectedMetrics), [
        metricsOptions,
        selectedMetrics,
    ]);

    // The creative query is seperated for 3 queries:
    // 1. For dimensions with the unique values
    // 2. For dimensions with the multivalued values
    // We do that to avoid getting duplicate creatives (with separating the multivalued dimensions)
    // And for showing results faster since the events and periods are heavy to fetch
    const [{ data: creativesResponse, isLoading }, runCreativesFetch] = useAsync();
    const [
        { data: creativesMultiValuedDimensionsResponse, isLoading: isLoadingDimensions },
        runCreativesMultiValuedDimensionsFetch,
    ] = useAsync();
    const [
        { data: lazyCreativesResponse, isLoading: isLoadingLazyCreatives, error: lazyCreativesError },
        runLazyCreativesFetch,
    ] = useAsync();
    const [{ data: lazyMultiValuedResponse }, runLazyMultiValuedFetch] = useAsync();
    const [{ data: creativeInsightsResponse, isLoading: isLoadingInsights }, runCreativeInsights] = useAsync();

    const canQueryCreatives = !!(
        dateRange &&
        app &&
        source &&
        ((metricsOptions && selectedMetrics.length) || (cohortsOptions && selectedCohorts.length))
    );

    const creatives = useMemo(() => {
        // During the initial-entry lazy fill, the parallel lazy responses (full metric + cohort set)
        // take over once they land; until then the fast eager responses drive the grid.
        const overlayActive = initialLazyFillActiveRef.current;
        const lazyLanded = overlayActive && !!lazyCreativesResponse;

        const baseResponse = lazyLanded ? lazyCreativesResponse : creativesResponse;
        const multiValuedResults =
            (lazyLanded ? lazyMultiValuedResponse?.results : creativesMultiValuedDimensionsResponse?.results) ??
            creativesMultiValuedDimensionsResponse?.results ??
            lastMultiValuedDimensions.current;

        const pendingMetrics = lazyLanded
            ? []
            : selectedMetrics.filter(({ value }) => !fetchedMetricsRef.current.has(value)).map(({ value }) => value);
        // While the parallel lazy fill is still loading, cohorts are deferred too, so blank them to
        // render loading skeletons instead of a stale 0.
        const pendingCohorts = overlayActive && !lazyLanded ? selectedCohorts.map(({ value }) => value) : [];

        if (!baseResponse && tempCreatives.current)
            return blankPendingFields(
                tempCreatives.current.filter(creative =>
                    hasValidMetrics(
                        creative.metrics,
                        creative.cohorts,
                        selectedMetrics,
                        selectedCohorts,
                        pendingMetrics,
                        pendingCohorts
                    )
                ),
                pendingMetrics,
                pendingCohorts
            );

        setFieldsMetadata(prev => baseResponse?.metadata || prev);

        return blankPendingFields(
            formatCreatives(
                baseResponse?.results,
                selectedMetrics,
                selectedCohorts,
                multiValuedResults,
                pendingMetrics,
                pendingCohorts
            ),
            pendingMetrics,
            pendingCohorts
        );
    }, [
        creativesResponse?.results,
        creativesMultiValuedDimensionsResponse?.results,
        lazyCreativesResponse?.results,
        lazyMultiValuedResponse?.results,
        selectedMetrics,
        selectedCohorts,
    ]);

    useEffect(() => {
        latestCreativesRef.current = creatives;
    }, [creatives]);

    useEffect(() => {
        if (initialLazyFillActiveRef.current && lazyCreativesResponse) {
            fetchedMetricsRef.current = new Set(fullMetricsToFetch.map(({ value }) => value));
        }
    }, [lazyCreativesResponse]);

    useEffect(() => {
        if (creativesResponse) haveCohortsChanged.current = false;
    }, [creativesResponse]);

    useEffect(() => {
        if (creativesMultiValuedDimensionsResponse?.results) {
            lastMultiValuedDimensions.current = creativesMultiValuedDimensionsResponse.results;
        }
    }, [creativesMultiValuedDimensionsResponse]);

    const filterToSetterMap = {
        [CreativeFilters.APP]: setApp,
        [CreativeFilters.SOURCE]: setSource,
        [CreativeFilters.METRICS]: metrics => {
            const orderedMetricsOptions = ALL_METRICS_OPTIONS.filter(metric => metrics.includes(metric.value));
            setSelectedMetrics(orderedMetricsOptions);
            setLocalStorageMetrics(metrics);
        },
        [CreativeFilters.DATE_RANGE]: setDateRange,
        [CreativeFilters.COHORTS]: cohorts => {
            // If the selected cohort updated after the first query has been launched, we need to save the creatives
            // last for now so that we can show them until the new query will return
            if (creativesResponse && creatives.length) {
                haveCohortsChanged.current = true;
                tempCreatives.current = creatives;
            }
            setSelectedCohorts(cohorts);
            setLocalStorageCohorts(cohorts.map(({ value }) => value));
        },
    };

    useEffect(() => {
        app && getDimensionsByCategory({ variables: { appId: app } });
    }, [getDimensionsByCategory, app]);

    useEffect(() => {
        dispatch(isCreativesGalleryReportRunning(isLoading));
    }, [isLoading]);

    const selectedMetricValues = useMemo(() => selectedMetrics.map(({ value }) => value), [selectedMetrics]);
    const selectedCohortValues = useMemo(() => selectedCohorts.map(({ value }) => value), [selectedCohorts]);

    const fetchCreativesData = additionalFilters => {
        if (!canQueryCreatives) return;

        setIsBadFilterCombination(false);
        setAdditionalFilters(additionalFilters || []);

        // On the first fetch of a link/local-storage restored selection, defer the heavy fields:
        // paint the eager base set now and load the lazy metrics + cohorts in parallel.
        const hasDeferrableSelection = selectedMetrics.some(({ isLazy }) => isLazy) || !!selectedCohorts.length;
        const isDeferredInitialLoad = pendingInitialDeferralRef.current && !additionalFilters && hasDeferrableSelection;
        if (isDeferredInitialLoad) pendingInitialDeferralRef.current = false;

        const metricsToFetch = isDeferredInitialLoad ? getEagerMetrics(metricsOptions) : fullMetricsToFetch;
        const cohortsToFetch = isDeferredInitialLoad ? [] : selectedCohorts;

        const params = [dateRange, app, source, metricsToFetch, cohortsToFetch];

        if (!additionalFilters) {
            setParamsObject({
                app,
                source,
                metrics: selectedMetricValues,
                cohortFields: selectedCohortValues,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
            });
        } else if (additionalFilters.length) params.push(additionalFilters);

        // Dedup on the user's full intent (not the eager subset) so the burst of init effects
        // collapses to a single fetch whether this turned into a deferred load.
        const requestSignature = JSON.stringify({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            app,
            source,
            metrics: fullMetricsToFetch.map(({ value }) => value),
            cohorts: selectedCohortValues,
            additionalFilters: additionalFilters || [],
        });

        if (requestSignature === inFlightSignatureRef.current) return;

        inFlightSignatureRef.current = requestSignature;

        const previousFetchedMetrics = fetchedMetricsRef.current;

        fetchedMetricsRef.current = new Set(metricsToFetch.map(({ value }) => value));

        const clearInFlightSignature = () => {
            if (inFlightSignatureRef.current === requestSignature) inFlightSignatureRef.current = null;
        };

        // A normal (non-deferred) fetch supersedes any active lazy fill so the main responses drive.
        initialLazyFillActiveRef.current = isDeferredInitialLoad;

        const requestSeq = requestGuard.current.next();
        const guardLatest = promise => requestGuard.current.guard(promise, requestSeq);

        const baseRequest = CreativeExploreAPI.runQuery(getMainCreativesQueryParams(...params), {
            suffix: 'base',
        });
        baseRequest.then(clearInFlightSignature, clearInFlightSignature);

        const guardedBaseRequest = guardLatest(baseRequest);
        guardedBaseRequest.catch(() => {
            fetchedMetricsRef.current = previousFetchedMetrics;
        });

        runCreativesFetch(guardedBaseRequest);
        runCreativesMultiValuedDimensionsFetch(
            guardLatest(
                CreativeExploreAPI.runQuery(getMultiValuedDimensionsQueryParams(...params), {
                    suffix: 'multiValued',
                })
            )
        );

        if (isDeferredInitialLoad) {
            const lazyParams = [dateRange, app, source, fullMetricsToFetch, selectedCohorts];
            const guardedLazyBase = guardLatest(
                CreativeExploreAPI.runQuery(getMainCreativesQueryParams(...lazyParams), { suffix: 'lazyBase' })
            );
            runLazyCreativesFetch(guardedLazyBase);
            runLazyMultiValuedFetch(
                guardLatest(
                    CreativeExploreAPI.runQuery(getMultiValuedDimensionsQueryParams(...lazyParams), {
                        suffix: 'lazyMultiValued',
                    })
                )
            );
        }
    };

    // Load creatives when multivalued filters are changed.
    const onAsyncLoad = filters => {
        if (!creatives.length) return;

        const nonEmptyFilters = filters.filter(({ values }) => values.length);

        if (!nonEmptyFilters.length) {
            originalMultiValuedDimensions.current = null;
        } else if (creativesMultiValuedDimensionsResponse && !originalMultiValuedDimensions.current) {
            originalMultiValuedDimensions.current = creativesMultiValuedDimensionsResponse.results;
        }

        // If the multivalued filter doesn't apply any druid row, we don't want to even try to query it
        // because it will return nothing, therefore we will just show the error.
        if (originalMultiValuedDimensions.current && nonEmptyFilters.length) {
            const updatedCreatives = originalMultiValuedDimensions.current.filter(creative =>
                nonEmptyFilters.every(filter => filter.values.includes(creative[filter.dimension]))
            );

            if (!updatedCreatives.length) {
                !isBadFilterCombination && setIsBadFilterCombination(true);
                return;
            }
        }

        fetchCreativesData(nonEmptyFilters);
        tempCreatives.current = creatives;
    };

    useEffect(() => {
        tempCreatives.current = null;
        originalMultiValuedDimensions.current = null;
        lastMultiValuedDimensions.current = null;
        fetchCreativesData();
    }, [dateRange, app, source]);

    useEffect(() => {
        if (!creativesResponse && !isLoading && !creativesMultiValuedDimensionsResponse && !isLoadingDimensions) {
            fetchCreativesData();
        } else if (canQueryCreatives) {
            const newMetricValues = fullMetricsToFetch
                .filter(({ value }) => !fetchedMetricsRef.current.has(value))
                .map(({ value }) => value);

            if (newMetricValues.length) {
                if (creatives.length) {
                    tempCreatives.current = blankPendingFields(creatives, newMetricValues);
                }
                fetchCreativesData();
            } else {
                setParamsObject({ metrics: selectedMetricValues });
            }
        }
    }, [selectedMetricValues]);

    // Load creatives when cohort metrics are selected (lazy loading).
    useEffect(() => {
        fetchCreativesData();
    }, [selectedCohortValues]);

    useEffect(() => {
        if (!initialLazyFillActiveRef.current || !lazyCreativesError || isLoading) return;

        initialLazyFillActiveRef.current = false;
        if (latestCreativesRef.current.length) tempCreatives.current = latestCreativesRef.current;
        inFlightSignatureRef.current = null;
        fetchCreativesData();
    }, [lazyCreativesError, isLoading]);

    useEffect(() => {
        if (app && source && dateRange.startDate && dateRange.endDate) {
            runCreativeInsights(ReportsAPI.getCreativeInsights(app, source, dateRange.startDate, dateRange.endDate));
        }
    }, [app, source, dateRange.startDate, dateRange.endDate]);

    const selectedAppOption = appOptions.find(option => option.value === app);
    const selectedSourceOption = sourceOptions.find(option => option.value === source);

    const { onCsvDownloadClicked, isLoadingCreativesTags, creativesTags, shouldCancelTagsFetch } = useCsvDownload({
        creatives,
        selectedAppOption,
        selectedSourceOption,
        dateRange,
        selectedCohorts,
        selectedTab,
        insightsDownloadUrl,
    });

    const onFilterChange = (field, value) => {
        if ([CreativeFilters.METRICS, CreativeFilters.COHORTS].includes(field)) {
            trackMixpanelEvent(TRACK_EVENT_PREFIX, `${field} Filter Changed`, {
                app,
                source,
                selected: (field === CreativeFilters.METRICS ? selectedMetrics : selectedCohorts).map(
                    ({ label }) => label
                ),
            });
        }

        filterToSetterMap[field](value);

        if (isLoadingCreativesTags) {
            shouldCancelTagsFetch.current = true;
        }
    };

    useEffect(() => {
        if (!isPageInitiated || !pageLoadingStartTime.current) return;

        const currentTime = new Date().getMilliseconds();
        const pageLoadStartTime = pageLoadingStartTime.current.getMilliseconds();
        const loadingTime = `${currentTime - pageLoadStartTime} milliseconds`;

        trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Page Loaded', {
            selectedTab,
            loadingTime,
        });
        pageLoadingStartTime.current = null;
    }, [isPageInitiated]);

    useEffect(() => {
        if (isPageInitiated) return;

        const urlParams = getParamsObject();

        const armDeferralIfHeavy = (metricValues, hasCohorts) => {
            if (containsLazyMetric(metricValues) || hasCohorts) pendingInitialDeferralRef.current = true;
        };

        if (!isObjectEmpty(urlParams)) {
            const {
                app: urlApp,
                source: urlSource,
                metrics: urlMetrics,
                startDate: urlStartDate,
                endDate: urlEndDate,
                creativeHash: urlCreativeHash,
                cohortFields: urlCohort,
                insightShelf: urlInsightShelf,
                tab: urlTab,
            } = urlParams;

            if (urlApp && !isLoadingApps) onFilterChange(CreativeFilters.APP, urlApp);
            if (urlSource && !isLoadingSources) onFilterChange(CreativeFilters.SOURCE, urlSource);
            if (urlMetrics && metricsOptions.length) onFilterChange(CreativeFilters.METRICS, urlMetrics);
            if (urlCohort && cohortsOptions?.length) {
                const urlCohortsSet = new Set(urlCohort);
                onFilterChange(
                    CreativeFilters.COHORTS,
                    cohortsOptions.filter(field => urlCohortsSet.has(field.value))
                );
            }

            armDeferralIfHeavy(urlMetrics, !!urlCohort?.length);
            if (urlStartDate && urlEndDate)
                onFilterChange(CreativeFilters.DATE_RANGE, { startDate: urlStartDate, endDate: urlEndDate });

            if (urlCreativeHash) setViewedCreativeHash(urlCreativeHash);
            if (urlInsightShelf) setOpenInsightsShelf(!!urlInsightShelf);
            if (urlTab) setSelectedTab(urlTab);
        } else if (!selectedMetrics.length && metricsOptions.length) {
            let metricsToSelect = [];
            if (localStorageMetrics.length) {
                const availableMetricsValues = new Set(metricsOptions.map(({ value }) => value));
                metricsToSelect = localStorageMetrics.filter(metric => availableMetricsValues.has(metric));
            }

            metricsToSelect = metricsToSelect.length ? metricsToSelect : DEFAULT_METRICS.map(({ value }) => value);

            onFilterChange(CreativeFilters.METRICS, metricsToSelect);

            armDeferralIfHeavy(metricsToSelect, !!localStorageCohorts.length);
        }

        if (!isLoadingApps && !isLoadingSources && metricsOptions.length) setIsPageInitiated(true);
    }, [isLoadingApps, isLoadingSources, metricsOptions]);

    useEffect(() => {
        if (!localStorageCohorts.length || !cohortsOptions.length) return;

        const localStorageCohortsSet = new Set(localStorageCohorts);

        const availableCohorts = cohortsOptions.filter(({ value }) => localStorageCohortsSet.has(value));

        if (!availableCohorts.length) return;

        availableCohorts.sort((a, b) => localStorageCohorts.indexOf(a.value) - localStorageCohorts.indexOf(b.value));
        onFilterChange(CreativeFilters.COHORTS, availableCohorts);
    }, [cohortsOptions]);

    // Auto-select app if only one is available
    useEffect(() => {
        if (isLoadingApps || app || appOptions.length !== 1) return;

        onFilterChange(CreativeFilters.APP, appOptions[0].value);
    }, [appOptions, app, isLoadingApps]);

    useEffect(() => {
        if (!isPageInitiated) return;

        const creativeHashParam = 'creativeHash';

        if (viewedCreativeHash) {
            setParamsObject({ [creativeHashParam]: viewedCreativeHash });
        } else {
            removeParam(creativeHashParam);
        }
    }, [viewedCreativeHash]);

    useEffect(() => {
        if (!isPageInitiated) return;

        setParamsObject({ tab: selectedTab });
    }, [selectedTab]);

    const viewedCreative = viewedCreativeHash
        ? creatives.find(({ imageHash }) => imageHash === viewedCreativeHash)
        : null;

    const onSortChange = value => {
        const metricOption = ALL_METRICS_OPTIONS.find(metric => metric.value === value);
        if (!metricOption) return;
        if (selectedMetrics.some(metric => metric.value === metricOption.value)) return;

        filterToSetterMap[CreativeFilters.METRICS](
            [...selectedMetrics, metricOption].map(({ value: metricValue }) => metricValue)
        );
    };

    const isAsyncLoading = (isLoading || isLoadingDimensions) && !!tempCreatives.current;
    const isResolvingInitialLazyFill =
        initialLazyFillActiveRef.current && (isLoadingLazyCreatives || !!lazyCreativesError);

    const displayedCreatives = useMemo(() => filteredCreatives.filter(({ hide }) => !hide), [filteredCreatives]);

    const onFilteredCreativesChange = updatedFilteredCreatives => {
        setFilteredCreatives(updatedFilteredCreatives || []);

        if (isLoadingDimensions !== false) return;

        const hasUpdatedCreatives = !!updatedFilteredCreatives.length;
        const hasPreviousCreatives = !!filteredCreatives.length;

        if (!isBadFilterCombination && !hasUpdatedCreatives) {
            setIsBadFilterCombination(true);
        } else if (isBadFilterCombination && hasUpdatedCreatives && !hasPreviousCreatives) {
            setIsBadFilterCombination(false);
        }
    };

    const handleCloseInsightsShelf = () => {
        setOpenInsightsShelf(false);
        removeParam('insightShelf');
    };

    const onExtendDateRange = () => {
        const endDate = new Date(dateRange.endDate);
        const newStartDate = new Date(endDate);
        newStartDate.setDate(endDate.getDate() - MIN_DATE_RANGE_DAYS + 1);

        setDateRange({
            startDate: newStartDate.toISOString().split('T')[0],
            endDate: dateRange.endDate,
        });
    };

    const onExpandSection = section => {
        const { startDate, endDate } = dateRange;
        trackMixpanelEvent(MIXPANEL_INSIGHTS_EVENT, 'Expanded', {
            app,
            source,
            startDate,
            endDate,
            section,
        });
    };

    return (
        <GalleryContext.Provider
            value={{
                dimensionsByCategory,
                creativesTags,
                fieldsMetadata,
                cohortPeriods,
                additionalFilters,
            }}
        >
            <PageWrapper className={css.pageWrapper} disablePadding>
                <CreativeGalleryHeader
                    apps={appOptions}
                    sources={sourceOptions}
                    metrics={metricsOptions}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    app={app}
                    source={source}
                    isLoadingApps={isLoadingApps}
                    isLoadingSources={isLoadingSources}
                    onFilterChange={onFilterChange}
                    selectedMetrics={selectedMetrics}
                    creativesAmount={creatives.length}
                    onCsvDownloadClicked={onCsvDownloadClicked}
                    hasQueryBeenDone={canQueryCreatives && (!!creativesResponse || isAsyncLoading)}
                    isLoadingCsvExport={isLoadingCreativesTags}
                    isLoadingMetrics={isLoadingMetrics}
                    cohortFieldsOptions={cohortsOptions}
                    selectedCohorts={selectedCohorts}
                    isLoadingCohorts={isLoadingCohorts}
                    cohortsGroupBy={cohortsGroupBy}
                    selectedTab={selectedTab}
                    onTabChange={setSelectedTab}
                    onExtendDateRange={onExtendDateRange}
                />
                {selectedTab === CREATIVES_TAB ? (
                    <div className={css.content} onScroll={e => gridRef.current?.onScroll(e)}>
                        <CreativeGalleryFilters
                            app={app}
                            source={source}
                            creatives={creatives}
                            selectedMetrics={selectedMetrics}
                            selectedCohorts={selectedCohorts}
                            disabled={!canQueryCreatives || (isLoading && !isAsyncLoading) || !creatives.length}
                            onFilteredCreativesChange={onFilteredCreativesChange}
                            isLoadingMultiGeneralDimensions={
                                isLoadingDimensions && !haveCohortsChanged.current && !isAsyncLoading
                            }
                            onAsyncLoad={onAsyncLoad}
                            isBadFilterCombination={isBadFilterCombination}
                        />
                        {!canQueryCreatives ? (
                            <TutorialSteps {...EMPTY_STATE_PARAMS} />
                        ) : !creatives.length && !isLoading && !isResolvingInitialLazyFill ? (
                            <TutorialSteps {...NO_DATA_PARAMS} />
                        ) : isBadFilterCombination ? (
                            <BadFilterCombination />
                        ) : (
                            <CreativesLayout
                                ref={gridRef}
                                isLoading={(isLoading && !isAsyncLoading) || !filteredCreatives.length}
                                loadingSections={getLoadingSections(
                                    isLoadingDimensions,
                                    isAsyncLoading,
                                    haveCohortsChanged.current,
                                    !!originalMultiValuedDimensions.current
                                )}
                                creatives={displayedCreatives}
                                onCreativeClick={setViewedCreativeHash}
                                selectedMetrics={selectedMetrics}
                                metricsOptions={metricsOptions}
                                selectedCohorts={selectedCohorts}
                                onSortChange={onSortChange}
                            />
                        )}
                    </div>
                ) : (
                    <CreativeInsightsTab
                        insights={creativeInsightsResponse}
                        isLoading={isLoadingInsights}
                        open={openInsightsShelf}
                        onClose={handleCloseInsightsShelf}
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        onExtendDateRange={onExtendDateRange}
                        onExpandSection={onExpandSection}
                        app={app}
                        source={source}
                        onInsightsDataReady={setInsightsDownloadUrl}
                    />
                )}
            </PageWrapper>
            <Fade in={!!viewedCreative}>
                <div className={css.creativePopupWrapper}>
                    {viewedCreative && (
                        <CreativePopup
                            app={selectedAppOption}
                            source={selectedSourceOption}
                            creative={viewedCreative}
                            selectedMetrics={selectedMetrics}
                            selectedCohorts={selectedCohorts}
                            dateRange={dateRange}
                            onClose={() => setViewedCreativeHash(null)}
                            onSimilarCreativeClick={setViewedCreativeHash}
                            isLoadingMetrics={isLoadingMetrics}
                            isLoadingCohorts={isLoadingCohorts}
                            displayedCreatives={displayedCreatives}
                        />
                    )}
                </div>
            </Fade>
        </GalleryContext.Provider>
    );
};

CreativeGallery.propTypes = {};

export default CreativeGallery;
